import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import PdfViewerModal from '../components/PdfViewerModal';
import Api from '../service/http';
import { hasActiveAccess } from '../utils/subscription';

const CourseDetails = () => {
  const { theme } = useSelector((state) => state.theme);
  const { user: contextUser } = useOutletContext() || {};
  const user = contextUser || JSON.parse(localStorage.getItem('user'));
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [pdfViewer, setPdfViewer] = useState(null);

  const subscribed = hasActiveAccess(user);

  const getCourse = async () => {
    setIsLoading(true);
    try {
      const res = await Api.get(`/app/course/${courseId}`);
      setItem(res.data);
    } catch (err) {
      console.error('Error fetching course details:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const openPdfModal = (fileUrl, name) => {
    if (!fileUrl) return;
    setPdfViewer({ url: fileUrl, title: name || 'Document' });
  };

  useEffect(() => {
    if (!subscribed) return;
    getCourse();
  }, [courseId, subscribed]);

  if (!subscribed) {
    return (
      <Navigate to="/courses" replace state={{ showSubscribeModal: true }} />
    );
  }

  return (
      <div className={`h-full overflow-hidden ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-white text-black'} px-4 py-6`}>
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
          >
            ← Back
          </button>
          <h1 className="text-xl font-semibold">{item?.name}</h1>
          <div className="w-10"></div>
        </div>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center mt-20">
            <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
            <p className={`mt-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Loading Course...</p>
          </div>
        ) : item ? (
          <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg p-6 shadow-sm h-[90%] overflow-y-auto scrollbar-hide`}>
            {item?.link && (
              <video
                controls
                src={item.link}
                className={`w-full h-64 rounded-md ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'} border mb-6`}
              />
            )}

            <div
              className={`prose ${theme === 'dark' ? 'prose-invert' : ''} max-w-none`}
              dangerouslySetInnerHTML={{
                __html: item.details || '<p>No content available</p>',
              }}
            />

            {item?.documents?.length > 0 && (
              <div className="mt-8 space-y-4">
                <h3 className="text-lg font-semibold">Documents</h3>
                {item.documents.map((doc, i) => (
                  <div
                    key={i}
                    className={`flex items-center justify-between p-4 border rounded-lg ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
                    }`}
                  >
                    <p className="text-sm font-medium truncate">{doc?.name}</p>
                    <button
                      type="button"
                      onClick={() => openPdfModal(doc?.file, doc?.name)}
                      className="bg-purple-600 text-white px-4 py-1.5 rounded hover:bg-purple-700"
                    >
                      View PDF
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>No course found.</p>
        )}

        {pdfViewer && (
          <PdfViewerModal
            url={pdfViewer.url}
            title={pdfViewer.title}
            onClose={() => setPdfViewer(null)}
          />
        )}
      </div>
  );
};

export default CourseDetails;
